/**
 * Paddle Service
 * Handles Paddle payment integration
 * 
 * Environment Variables:
 * - VUE_APP_PADDLE_TOKEN: Your Paddle token (preferred for Paddle Billing)
 * - VUE_APP_PADDLE_SELLER_ID: Your Paddle Seller ID (alternative)
 * - VUE_APP_PADDLE_VENDOR_ID: Your Paddle Vendor ID (legacy, alternative)
 * - VUE_APP_PADDLE_ENVIRONMENT: 'sandbox' or 'production' (default: 'sandbox')
 * - VUE_APP_PADDLE_PRODUCT_ID: Paddle product ID (optional)
 * 
 * Note: Paddle Billing uses token or Seller ID with Paddle.Initialize()
 */

class PaddleService {
  constructor() {
    // Priority: token > sellerId > vendorId
    this.token = process.env.VUE_APP_PADDLE_TOKEN || null;
    this.sellerId = process.env.VUE_APP_PADDLE_SELLER_ID || process.env.VUE_APP_PADDLE_VENDOR_ID || null;
    this.environment = process.env.VUE_APP_PADDLE_ENVIRONMENT || 'sandbox';
    this.productId = process.env.VUE_APP_PADDLE_PRODUCT_ID || null;
    this.priceId = process.env.VUE_APP_PADDLE_PRICESID || null; // Price ID for Paddle Billing
    this.isInitialized = false;
    this.paddleInstance = null;
    
    // Log configuration for debugging
    if (this.token || this.sellerId) {
      console.log('🔧 Paddle Configuration:', {
        hasToken: !!this.token,
        token: this.token ? `${this.token.substring(0, 10)}...` : null,
        sellerId: this.sellerId ? `${this.sellerId.substring(0, 3)}...` : null,
        environment: this.environment,
        productId: this.productId || 'Not set',
        priceId: this.priceId || 'Not set (will use dynamic pricing)'
      });
    } else {
      console.warn('⚠️ Paddle credentials not found in environment variables');
      console.warn('💡 Set VUE_APP_PADDLE_TOKEN, VUE_APP_PADDLE_SELLER_ID, or VUE_APP_PADDLE_VENDOR_ID in your .env');
    }
  }

  /**
   * Initialize Paddle SDK
   * Uses Paddle.Initialize() with Seller ID (new Paddle Billing API)
   */
  async initialize() {
    if (this.isInitialized) {
      return this.paddleInstance;
    }

    // Check if we have any credentials (token, sellerId, or vendorId)
    if (!this.token && !this.sellerId) {
      console.warn('Paddle credentials not configured. Please set VUE_APP_PADDLE_TOKEN, VUE_APP_PADDLE_SELLER_ID, or VUE_APP_PADDLE_VENDOR_ID');
      return null;
    }

    return new Promise((resolve, reject) => {
      // Check if Paddle script is already loaded
      if (window.Paddle) {
        this.initializePaddleInstance();
        if (this.isInitialized) {
          resolve(this.paddleInstance);
        } else {
          reject(new Error('Failed to initialize Paddle'));
        }
        return;
      }

      // Load Paddle script
      const script = document.createElement('script');
      script.src = 'https://cdn.paddle.com/paddle/v2/paddle.js';
      
      script.onload = () => {
        // Wait a bit for Paddle to be fully available
        setTimeout(() => {
          if (window.Paddle) {
            this.initializePaddleInstance();
            if (this.isInitialized) {
              resolve(this.paddleInstance);
            } else {
              reject(new Error('Failed to initialize Paddle instance'));
            }
          } else {
            console.error('❌ window.Paddle is not available after script load');
            reject(new Error('Paddle SDK failed to load - window.Paddle not found'));
          }
        }, 200);
      };

      script.onerror = () => {
        reject(new Error('Failed to load Paddle SDK script'));
      };

      document.head.appendChild(script);
    });
  }

  /**
   * Initialize Paddle instance with correct API
   */
  initializePaddleInstance() {
    try {
      this.paddleInstance = window.Paddle;
      
      // Set environment first (sandbox or production)
      if (this.environment === 'sandbox') {
        if (this.paddleInstance.Environment && this.paddleInstance.Environment.set) {
          this.paddleInstance.Environment.set('sandbox');
          console.log('🌐 Paddle environment set to sandbox');
        }
      }

      // Try new Paddle Billing API first (Paddle.Initialize)
      if (this.paddleInstance.Initialize) {
        try {
          // Priority: token > sellerId
          if (this.token) {
            // Use token (preferred method)
            this.paddleInstance.Initialize({ 
              token: this.token 
            });
            console.log('✅ Paddle initialized with token (new API)');
            this.isInitialized = true;
            return;
          } else if (this.sellerId) {
            // Use seller ID
            this.paddleInstance.Initialize({ 
              seller: this.sellerId 
            });
            console.log('✅ Paddle initialized with Seller ID (new API)');
            this.isInitialized = true;
            return;
          }
        } catch (initError) {
          console.error('❌ Paddle.Initialize() failed:', initError.message);
          console.error('❌ Error details:', initError);
          throw initError;
        }
      } 
      
      // Fallback to old API (Paddle.Setup) for compatibility
      if (this.paddleInstance.Setup) {
        console.warn('⚠️ Using legacy Paddle.Setup() API');
        this.paddleInstance.Setup({ vendor: parseInt(this.sellerId) });
        this.isInitialized = true;
        console.log('✅ Paddle initialized with legacy Setup()');
        return;
      }
      
      throw new Error('Neither Paddle.Initialize() nor Paddle.Setup() is available');
    } catch (error) {
      console.error('❌ Error initializing Paddle:', error);
      console.error('❌ Error details:', {
        message: error.message,
        stack: error.stack,
        paddleAvailable: !!window.Paddle,
        hasInitialize: !!(window.Paddle && window.Paddle.Initialize),
        hasSetup: !!(window.Paddle && window.Paddle.Setup)
      });
      this.isInitialized = false;
      throw error;
    }
  }

  /**
   * Open Paddle Checkout
   * @param {Object} options - Checkout options
   * @param {string} options.productId - Paddle product ID
   * @param {string} options.priceId - Paddle price ID (preferred for Paddle Billing)
   * @param {number} options.price - Price in cents (or MAD converted)
   * @param {string} options.email - Customer email
   * @param {string} options.successUrl - Success redirect URL
   * @param {string} options.customData - Custom data to pass
   */
  async openCheckout(options = {}) {
    try {
      const paddle = await this.initialize();
      if (!paddle) {
        throw new Error('Paddle not initialized');
      }

      if (!paddle.Checkout || !paddle.Checkout.open) {
        throw new Error('Paddle.Checkout.open is not available');
      }

      const {
        productId,
        priceId,
        price,
        email,
        successUrl,
        customData = {}
      } = options;

      // Priority: priceId from options > priceId from env > productId from options > productId from env
      const finalPriceId = priceId || this.priceId;
      const finalProductId = productId || this.productId;
      
      console.log('🛒 Opening Paddle checkout with options:', {
        hasPriceId: !!finalPriceId,
        hasProductId: !!finalProductId,
        hasPrice: !!price,
        hasEmail: !!email,
        priceInCents: price,
        priceId: finalPriceId,
        productId: finalProductId
      });

      // Paddle checkout configuration
      const checkoutOptions = {
        successCallback: (data) => {
          console.log('✅ Paddle checkout success:', data);
          if (successUrl) {
            window.location.href = successUrl;
          }
          // Trigger custom event
          window.dispatchEvent(new CustomEvent('paddle:checkout:success', { detail: data }));
        },
        closeCallback: () => {
          console.log('ℹ️ Paddle checkout closed by user');
          window.dispatchEvent(new CustomEvent('paddle:checkout:close'));
        },
        errorCallback: (error) => {
          console.error('❌ Paddle checkout error callback triggered');
          console.error('❌ Error object:', error);
          console.error('❌ Error type:', typeof error);
          console.error('❌ Error keys:', error ? Object.keys(error) : 'null');
          
          // Extract error message
          let errorMessage = 'Une erreur est survenue lors de l\'ouverture du checkout Paddle';
          if (error) {
            if (typeof error === 'string') {
              errorMessage = error;
            } else if (error.message) {
              errorMessage = error.message;
            } else if (error.error) {
              errorMessage = error.error;
            } else {
              errorMessage = JSON.stringify(error);
            }
          }
          
          console.error('❌ Extracted error message:', errorMessage);
          
          // Trigger custom event with detailed error
          window.dispatchEvent(new CustomEvent('paddle:checkout:error', { 
            detail: {
              error: error,
              message: errorMessage,
              timestamp: new Date().toISOString()
            }
          }));
        }
      };

      // Add email if provided
      if (email) {
        checkoutOptions.email = email;
      }

      // Add passthrough data if provided
      if (Object.keys(customData).length > 0) {
        checkoutOptions.passthrough = JSON.stringify({
          userId: customData.userId || null,
          sensorCount: customData.sensorCount || null,
          ...customData
        });
      }

      // Priority: Use Price ID (preferred for Paddle Billing)
      if (finalPriceId) {
        checkoutOptions.items = [
          {
            priceId: finalPriceId,
            quantity: 1
          }
        ];
        console.log('📦 Using Paddle Price ID:', finalPriceId);
        console.log('ℹ️ Note: Price ID already has a fixed price. Price override will be ignored.');
        // Don't override price when using Price ID - the Price ID has its own price
      } 
      // Fallback: Use Product ID
      else if (finalProductId) {
        checkoutOptions.product = finalProductId;
        console.log('📦 Using Paddle product ID:', finalProductId);
        
        // If custom price is provided, override the price (only for Product ID, not Price ID)
        if (price) {
          checkoutOptions.override = {
            price: {
              amount: price,
              currency: 'MAD'
            }
          };
          console.log('💰 Overriding product price with:', price, 'cents (MAD)');
        }
      }
      // If no Price ID or Product ID, try price-only checkout
      else if (price) {
        console.warn('⚠️ No Price ID or Product ID provided, using price override only');
        console.warn('⚠️ This may not work in all Paddle configurations. Consider creating a Price ID in Paddle Dashboard.');
        checkoutOptions.override = {
          price: {
            amount: price,
            currency: 'MAD'
          }
        };
        console.log('💰 Using dynamic pricing only:', price, 'cents (MAD)');
      }

      // Validate that we have at least priceId, productId, or price
      if (!finalPriceId && !finalProductId && !price) {
        throw new Error('Either priceId, productId, or price must be provided');
      }

      // Log full configuration (sanitized for sensitive data)
      const logConfig = {
        items: checkoutOptions.items ? JSON.stringify(checkoutOptions.items) : 'Not set',
        product: checkoutOptions.product || 'Not set',
        email: checkoutOptions.email ? `${checkoutOptions.email.substring(0, 3)}...` : 'Not set',
        passthrough: checkoutOptions.passthrough ? 'Set' : 'Not set',
        override: checkoutOptions.override ? JSON.stringify(checkoutOptions.override) : 'Not set',
        hasSuccessCallback: !!checkoutOptions.successCallback,
        hasErrorCallback: !!checkoutOptions.errorCallback,
        hasCloseCallback: !!checkoutOptions.closeCallback
      };
      
      console.log('🚀 Opening Paddle checkout with config:', logConfig);
      console.log('🔍 Full checkout options (for debugging):', JSON.stringify(checkoutOptions, null, 2));

      try {
        // Verify Paddle.Checkout.open exists
        if (!paddle.Checkout || typeof paddle.Checkout.open !== 'function') {
          throw new Error('Paddle.Checkout.open is not a function. Paddle may not be properly initialized.');
        }
        
        console.log('🔄 Calling Paddle.Checkout.open()...');
        paddle.Checkout.open(checkoutOptions);
        console.log('✅ Paddle.Checkout.open() called successfully (checkout should open)');
      } catch (openError) {
        console.error('❌ Error calling Paddle.Checkout.open():', openError);
        console.error('❌ Error name:', openError.name);
        console.error('❌ Error message:', openError.message);
        console.error('❌ Error stack:', openError.stack);
        throw new Error(`Failed to open Paddle checkout: ${openError.message || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('❌ Error opening Paddle checkout:', error);
      console.error('❌ Error stack:', error.stack);
      throw error;
    }
  }

  /**
   * Open Paddle Subscription Checkout
   * @param {Object} options - Subscription options
   */
  async openSubscriptionCheckout(options = {}) {
    try {
      const paddle = await this.initialize();
      if (!paddle) {
        throw new Error('Paddle not initialized');
      }

      const {
        planId,
        email,
        successUrl,
        customData = {}
      } = options;

      paddle.Checkout.open({
        product: planId,
        email: email,
        passthrough: JSON.stringify({
          userId: customData.userId || null,
          sensorCount: customData.sensorCount || null,
          subscriptionType: 'annual',
          ...customData
        }),
        successCallback: (data) => {
          console.log('Paddle subscription success:', data);
          if (successUrl) {
            window.location.href = successUrl;
          }
          window.dispatchEvent(new CustomEvent('paddle:subscription:success', { detail: data }));
        },
        closeCallback: () => {
          console.log('Paddle subscription checkout closed');
          window.dispatchEvent(new CustomEvent('paddle:subscription:close'));
        }
      });
    } catch (error) {
      console.error('Error opening Paddle subscription checkout:', error);
      throw error;
    }
  }

  /**
   * Update subscription (for managing existing subscriptions)
   * @param {string} subscriptionId - Paddle subscription ID
   */
  async updateSubscription(subscriptionId) {
    try {
      const paddle = await this.initialize();
      if (!paddle) {
        throw new Error('Paddle not initialized');
      }

      // Open subscription management
      paddle.Subscription.update({
        subscriptionId: subscriptionId,
        successCallback: (data) => {
          console.log('Subscription updated:', data);
          window.dispatchEvent(new CustomEvent('paddle:subscription:updated', { detail: data }));
        }
      });
    } catch (error) {
      console.error('Error updating subscription:', error);
      throw error;
    }
  }

  /**
   * Cancel subscription
   * @param {string} subscriptionId - Paddle subscription ID
   */
  async cancelSubscription(subscriptionId) {
    try {
      const paddle = await this.initialize();
      if (!paddle) {
        throw new Error('Paddle not initialized');
      }

      paddle.Subscription.cancel({
        subscriptionId: subscriptionId,
        successCallback: (data) => {
          console.log('Subscription cancelled:', data);
          window.dispatchEvent(new CustomEvent('paddle:subscription:cancelled', { detail: data }));
        }
      });
    } catch (error) {
      console.error('Error cancelling subscription:', error);
      throw error;
    }
  }

  /**
   * Convert MAD to cents (Paddle uses cents)
   * @param {number} madAmount - Amount in MAD
   * @returns {number} Amount in cents
   */
  convertMADToCents(madAmount) {
    // Paddle uses cents, so multiply by 100
    return Math.round(madAmount * 100);
  }

  /**
   * Get Paddle environment
   */
  getEnvironment() {
    return this.environment;
  }

  /**
   * Check if Paddle is initialized
   */
  isReady() {
    return this.isInitialized && this.paddleInstance !== null;
  }

  /**
   * Get initialization status for debugging
   */
  getStatus() {
    return {
      hasToken: !!this.token,
      token: this.token ? `${this.token.substring(0, 10)}...` : null,
      sellerId: this.sellerId ? `${this.sellerId.substring(0, 3)}...` : null,
      environment: this.environment,
      isInitialized: this.isInitialized,
      paddleInstance: !!this.paddleInstance,
      windowPaddle: !!window.Paddle,
      productId: this.productId,
      priceId: this.priceId,
      hasInitialize: !!(window.Paddle && window.Paddle.Initialize),
      hasSetup: !!(window.Paddle && window.Paddle.Setup)
    };
  }
}

// Export singleton instance
export const paddleService = new PaddleService();

// Export class for testing
export default PaddleService;

